/**
 * Wyoming State-Specific Diagnostic Questions
 * COMPLETE REBUILD - Using correct Chapter 9 and Chapter 10 citations
 * 
 * CRITICAL CORRECTIONS FROM ORIGINAL FILE:
 * 1. Changed all "Rules Chapter 8" references - Chapter 8 is for Boarding Homes/FDTC, NOT Home Health
 * 2. Correct regulations: Chapter 9 (Program Administration) and Chapter 10 (Licensure)
 * 3. Fixed phone number: (307) 777-7123 (not 777-7656)
 * 4. Removed fabricated "W.S. 35-2-1001" - doesn't exist; background checks in Ch. 9 §5(a)(ii)
 * 5. Corrected supervision frequencies per actual regulations
 * 
 * CORRECT WYOMING HOME HEALTH AGENCY REGULATIONS:
 * - Chapter 9: Program Administration of Home Health Agencies (eff. 10-15-2001)
 *   - Section 1: Authority
 *   - Section 2: Purpose  
 *   - Section 3: Severability
 *   - Section 4: Definitions
 *   - Section 5: Organization and Administration
 *   - Section 6: Home Health Aide
 *   - Section 7: Homemaker
 *   - Section 8: Client Records
 *   - Section 9: Licensing
 * 
 * - Chapter 10: Licensure of Home Health Agency (eff. 11-01-2001)
 *   - Section 1-3: Authority, Purpose, Severability
 *   - Section 4: Definitions
 *   - Section 5: Licensure
 * 
 * RELATED STATUTES:
 * - W.S. 35-2-901 through 35-2-913: Health Facilities Act
 * - W.S. 9-2-1201 et seq.: Senior Citizens Act
 * - W.S. 14-3-213: Central Registry (child abuse/neglect)
 * - W.S. 35-20-101 through 35-20-116: Adult Protective Services
 * 
 * VERIFIED SOURCES:
 * - https://health.wyo.gov/aging/hls/rules-and-regulations/
 * - https://health.wyo.gov/wp-content/uploads/2016/11/HLS-Rule-Ch-9-Home-Health.pdf
 * - https://health.wyo.gov/wp-content/uploads/2016/11/HLS-Rule-Ch-10-Home-Health-Agency.pdf
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const WYOMING_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & GOVERNANCE
  // ============================================
  {
    id: 'WY-A1',
    itemId: 'A1',
    stateCode: 'WY',
    title: 'Home Health Agency License Status',
    description: 'Wyoming requires all home health agencies to be licensed under Chapter 10',
    severity: 'high',
    regulationCode: 'Chapter 10, Section 5',
    regulationName: 'Licensure of Home Health Agency',
    agencyName: 'Wyoming Department of Health, Healthcare Licensing and Surveys',
    agencyAbbr: 'WDH HLS',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from the Wyoming Department of Health?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home health agency licenses run July 1 - June 30 annually per Chapter 10, Section 5(a)(ii)',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        helpText: 'Wyoming HHA licenses expire June 30th annually',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Is your current license displayed in a public area within the agency?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Chapter 10, Section 5(f)',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Wyoming Home Health Agency license is current and active per Chapter 10.',
      atRisk: 'Your license expires soon. Wyoming licenses run July 1 - June 30. Submit renewal application before expiration.',
      nonCompliant: 'Operating without a valid WDH license violates W.S. 35-2-901 et seq. Contact HLS immediately at (307) 777-7123.',
      unknown: 'Verify your license status with WDH Healthcare Licensing and Surveys at (307) 777-7123.',
    },
    stateNotes: [
      'Licenses issued July 1 - June 30 annually (Chapter 10, §5(a)(ii))',
      'Full fee due whether issued for entire year or part of year',
      'License must be displayed in public area (Chapter 10, §5(f))',
      'Provisional license may be issued during initial compliance period',
      'License not transferable without prior written approval (Chapter 10, §5(c))',
    ],
  },

  {
    id: 'WY-A2',
    itemId: 'A2',
    stateCode: 'WY',
    title: 'Governing Body Requirements',
    description: 'Wyoming requires a governing body with legal authority per Chapter 9',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 5(a)',
    regulationName: 'Organization and Administration - Governing Body',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a governing body with legal authority and responsibility to operate the agency?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per Chapter 9, Section 5(a)',
      },
      {
        id: 'q2',
        question: 'Does your governing body fulfill all required responsibilities?',
        inputType: 'checklist',
        options: [
          { value: 'fidelity_bond', label: 'Maintains fidelity bond (minimum $2,500) for client protection' },
          { value: 'central_registry', label: 'Verifies Central Registry check on all employees' },
          { value: 'personnel_policies', label: 'Adopts and revises personnel policies including evaluation frequency' },
          { value: 'org_chart', label: 'Prepares organizational chart showing administrative control' },
          { value: 'admin_appointed', label: 'Appoints qualified administrator designated in writing' },
          { value: 'supervisory_nurse', label: 'Employs qualified supervisory nurse (RN)' },
          { value: 'qm_program', label: 'Develops written quality management program' },
          { value: 'grievance', label: 'Establishes grievance procedure' },
        ],
        helpText: 'Requirements per Chapter 9, Section 5(a)(i)-(viii)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 7 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Governing body meets Chapter 9, Section 5(a) requirements.',
      atRisk: 'Governing body may not fulfill all required responsibilities. Review Chapter 9, Section 5(a).',
      nonCompliant: 'No governing body or inadequate governance structure violates Chapter 9.',
      unknown: 'Review governing body requirements with WDH.',
    },
    stateNotes: [
      'Fidelity bond minimum $2,500, augmented by number of employees (§5(a)(i))',
      'Central Registry verification required for all employees (§5(a)(ii))',
      'Must ensure confidentiality of Central Registry information',
      'Quality management program required per W.S. 35-2-910',
    ],
  },

  {
    id: 'WY-A3',
    itemId: 'A3',
    stateCode: 'WY',
    title: 'Administrator Qualifications',
    description: 'Wyoming requires a qualified administrator designated in writing per Chapter 9',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 5(a)(v)',
    regulationName: 'Administrator',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Does your administrator meet Wyoming qualification requirements?',
        inputType: 'select',
        options: [
          { value: 'physician', label: 'Licensed physician' },
          { value: 'rn', label: 'Registered nurse' },
          { value: 'bachelor_3yr', label: 'Bachelor\'s degree + 3 years health care management experience' },
          { value: 'no_degree_6yr', label: 'No degree + 6 years supervisory health care management experience' },
          { value: 'not_qualified', label: 'Does not meet any qualification' },
        ],
        required: true,
        helpText: 'Qualifications per Chapter 9, Section 5(a)(v)(A)',
      },
      {
        id: 'q2',
        question: 'Is the administrator designated in writing as responsible and available for all aspects of agency operation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per Chapter 9, Section 5(a)(v)',
      },
      {
        id: 'q3',
        question: 'Has the administrator identified in writing a qualified individual authorized to act when administrator is not available?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per Chapter 9, Section 5(a)(v)(C)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'not_qualified', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'notEquals', questionId: 'q1', value: 'not_qualified' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator qualifications and documentation meet Chapter 9, Section 5(a)(v) requirements.',
      atRisk: 'Administrator documentation incomplete. Must be designated in writing with backup identified.',
      nonCompliant: 'Administrator does not meet Wyoming qualification requirements. This is a critical deficiency.',
      unknown: 'Verify administrator qualifications against Chapter 9, Section 5(a)(v).',
    },
    stateNotes: [
      'Qualified administrator: Licensed physician, RN, or bachelor\'s + 3 yrs, OR 6 yrs supervisory experience',
      'Administrator and supervisory nurse may be same person if dually qualified (§5(a)(v)(B))',
      'Must identify in writing a qualified backup when administrator unavailable (§5(a)(v)(C))',
      'Must be designated in writing as responsible for all aspects of agency operation',
    ],
  },

  {
    id: 'WY-A4',
    itemId: 'A4',
    stateCode: 'WY',
    title: 'Supervisory Nurse Requirements',
    description: 'Wyoming requires a supervisory nurse (RN) with home health experience per Chapter 9',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 5(a)(vi)',
    regulationName: 'Supervisory Nurse Requirements',
    agencyName: 'Wyoming State Board of Nursing',
    agencyAbbr: 'WSBN',
    agencyPhone: '(307) 777-7601',
    agencyUrl: 'https://wsbn.wyo.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you employ a supervisory nurse who is a registered nurse?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your supervisory nurse have at least one year of home health experience?',
        inputType: 'select',
        options: [
          { value: 'yes_experience', label: 'Yes, has 1+ year home health experience' },
          { value: 'consultation', label: 'No, but has consultation agreement in place' },
          { value: 'no', label: 'No experience and no consultation agreement' },
        ],
        helpText: 'Per Chapter 9, Section 5(a)(vi), consultation agreement may substitute until experience gained',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Is the supervisory nurse available during normal hours of operation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      // NON_COMPLIANT rules FIRST
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      // COMPLIANT only if all requirements met
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'includes', questionId: 'q2', value: ['yes_experience', 'consultation'] },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Supervisory nurse meets Chapter 9, Section 5(a)(vi) requirements.',
      atRisk: 'Supervisory nurse may not meet all requirements.',
      nonCompliant: 'No qualified supervisory nurse OR nurse not available during normal hours. This violates Chapter 9, Section 5(a)(vi).',
      unknown: 'Verify supervisory nurse qualifications.',
    },
    stateNotes: [
      'Must be RN with at least 1 year home health experience',
      'Consultation agreement may substitute for experience until gained',
      'Consultation agreement must be in writing, signed by all parties',
      'Must enable immediate contact 24/7 with experienced RN',
      'Consultations must be documented and on file',
    ],
  },

  {
    id: 'WY-A5',
    itemId: 'A5',
    stateCode: 'WY',
    title: 'Fidelity Bond Requirement',
    description: 'Wyoming requires a fidelity bond for client protection per Chapter 9',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 5(a)(i)',
    regulationName: 'Fidelity Bond Requirements',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency maintain a fidelity bond for client protection?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per Chapter 9, Section 5(a)(i)',
      },
      {
        id: 'q2',
        question: 'What is your fidelity bond coverage amount?',
        inputType: 'select',
        options: [
          { value: '2500_plus', label: '$2,500 or more (augmented by employee count)' },
          { value: 'less_2500', label: 'Less than $2,500' },
          { value: 'unknown', label: 'Unknown' },
        ],
        helpText: 'Minimum $2,500 required, should be augmented based on number of employees',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: '2500_plus' },
      ], result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['less_2500', 'unknown'], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Fidelity bond meets Chapter 9, Section 5(a)(i) requirements.',
      atRisk: 'Fidelity bond may be insufficient. Minimum $2,500 required, augmented by employee count.',
      nonCompliant: 'No fidelity bond. This violates Chapter 9, Section 5(a)(i).',
      unknown: 'Verify fidelity bond coverage amount.',
    },
    stateNotes: [
      'Minimum fidelity bond: $2,500 per Chapter 9, Section 5(a)(i)',
      'Bond amount should be augmented based on number of employees',
      'Purpose: client protection from employee theft or misappropriation',
      'Governing body responsible for maintaining adequate bond coverage',
    ],
  },

  {
    id: 'WY-A6',
    itemId: 'A6',
    stateCode: 'WY',
    title: 'Quality Management Program',
    description: 'Wyoming requires an ongoing agency-wide quality management program per Chapter 9',
    severity: 'medium',
    regulationCode: 'Chapter 9, Section 5(a)(vii) / W.S. 35-2-910',
    regulationName: 'Quality Management Requirements',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a written quality management program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per Chapter 9, Section 5(a)(vii) and W.S. 35-2-910',
      },
      {
        id: 'q2',
        question: 'Does your QM program include the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'ongoing', label: 'Ongoing (not just periodic) evaluation' },
          { value: 'agency_wide', label: 'Agency-wide scope (all services)' },
          { value: 'written', label: 'Written policies and procedures' },
          { value: 'ensures_quality', label: 'Ensures quality of care provided' },
          { value: 'evaluates', label: 'Evaluates quality of care provided' },
          { value: 'all_clients', label: 'Covers all clients receiving services' },
        ],
        helpText: 'Elements required per Chapter 9, Section 5(a)(vii)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality management program meets Chapter 9, Section 5(a)(vii) requirements.',
      atRisk: 'Quality management program may be incomplete.',
      nonCompliant: 'No quality management program. This violates Chapter 9 and W.S. 35-2-910.',
      unknown: 'Review QM program requirements.',
    },
    stateNotes: [
      'QM program must be ongoing, not just periodic per Chapter 9, Section 5(a)(vii)',
      'Must be agency-wide in scope covering all services',
      'Written policies and procedures required',
      'Must ensure AND evaluate quality of care for all clients',
      'Required by W.S. 35-2-910 (Health Facilities Act)',
    ],
  },

  {
    id: 'WY-A7',
    itemId: 'A7',
    stateCode: 'WY',
    title: 'Grievance Procedure',
    description: 'Wyoming requires a written grievance procedure per Chapter 9',
    severity: 'medium',
    regulationCode: 'Chapter 9, Section 5(a)(viii)',
    regulationName: 'Grievance Procedure Requirements',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Does your grievance procedure include all Wyoming-required elements?',
        inputType: 'checklist',
        options: [
          { value: 'voice_method', label: 'Method for clients to voice grievances' },
          { value: 'documentation', label: 'Documentation of agency response to verbal and written grievances' },
          { value: 'contact_list', label: 'List of appropriate agencies with addresses and phone numbers' },
          { value: 'unresolved_reports', label: 'Written reports of unresolved grievances to Licensing Division within 10 days' },
          { value: 'resolved_on_file', label: 'Resolved grievances and resolutions kept on file' },
        ],
        required: true,
        helpText: 'Requirements per Chapter 9, Section 5(a)(viii)(A)',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Grievance procedure meets Chapter 9, Section 5(a)(viii) requirements.',
      atRisk: 'Grievance procedure may be missing required elements.',
      nonCompliant: 'Grievance procedure is inadequate. Review Chapter 9, Section 5(a)(viii).',
      unknown: 'Review grievance procedure requirements.',
    },
    stateNotes: [
      'Unresolved grievances must be reported to Licensing Division within 10 days',
      'If 10-day requirement cannot be met, contact Licensing Division',
      'Must include list of appropriate agencies for escalation',
      'Client complaints referred to State Long Term Care Ombudsman in writing',
    ],
  },

  {
    id: 'WY-A8',
    itemId: 'A8',
    stateCode: 'WY',
    title: 'Client Rights',
    description: 'Wyoming requires specific client rights protections per Chapter 9',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 5(a)(xiii)',
    regulationName: 'Client Rights Requirements',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide clients with information about their rights including:',
        inputType: 'checklist',
        options: [
          { value: 'dignity', label: 'Right to be treated with dignity, consideration and respect' },
          { value: 'property', label: 'Right to have property treated with respect' },
          { value: 'timely', label: 'Right to timely response to service requests' },
          { value: 'informed_costs', label: 'Right to be fully informed of care, treatment, and costs upon admission' },
          { value: 'informed_changes', label: 'Right to be informed in advance of care changes' },
          { value: 'payment_notice', label: 'Right to be informed in advance of payment responsibility' },
          { value: 'trained_personnel', label: 'Right to care from professionally trained personnel' },
          { value: 'participate', label: 'Right to participate in care plan design and updates' },
          { value: 'confidentiality', label: 'Right to confidentiality of information' },
          { value: 'termination_notice', label: 'Right to reasonable notice of service termination' },
          { value: 'discontinue', label: 'Right to authorize discontinuation of treatment' },
          { value: 'complaints', label: 'Right to make complaints and recommend policy changes' },
          { value: 'hotline', label: 'Information about home health hotline number' },
        ],
        required: true,
        helpText: 'Client rights per Chapter 9, Section 5(a)(xiii)(A)',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 12, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 8, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 8, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client rights information meets Chapter 9, Section 5(a)(xiii) requirements.',
      atRisk: 'Client rights information may be incomplete.',
      nonCompliant: 'Inadequate client rights information violates Chapter 9.',
      unknown: 'Review client rights requirements.',
    },
    stateNotes: [
      'Home Health and Hospice Hotline: 1-800-548-1367',
      'Hotline available Monday-Friday 8am-5pm, excluding state holidays',
      'Clients must be notified if agency is not Medicare/Medicaid certified (§5(a)(xiv)(A))',
      'Responsible party notified of charges at admission and 30 days before changes',
    ],
  },

  {
    id: 'WY-A9',
    itemId: 'A9',
    stateCode: 'WY',
    title: 'Advanced Directives Policy',
    description: 'Wyoming requires policies assuring information on advanced directives is provided',
    severity: 'medium',
    regulationCode: 'Chapter 9, Section 5(a)(xii)',
    regulationName: 'Advanced Directives Requirements',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have policies that assure information on advanced directives is provided to clients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per Chapter 9, Section 5(a)(xii)',
      },
      {
        id: 'q2',
        question: 'If a client\'s advanced directives are known, are they followed by the agency?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Chapter 9, Section 5(a)(xii)(A)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Advanced directives policies meet Chapter 9 requirements.',
      atRisk: 'Advanced directives may not be consistently followed.',
      nonCompliant: 'No advanced directives policies. This violates Chapter 9, Section 5(a)(xii).',
      unknown: 'Review advanced directives requirements.',
    },
    stateNotes: [
      'Must provide information on advanced directives to clients per Chapter 9, Section 5(a)(xii)',
      'Agency must follow client advanced directives when known per Section 5(a)(xii)(A)',
      'Wyoming Healthcare Decisions Act: W.S. 35-22-401 et seq.',
      'Living wills and healthcare powers of attorney recognized in Wyoming',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND CHECKS
  // ============================================
  {
    id: 'WY-B1',
    itemId: 'B1',
    stateCode: 'WY',
    title: 'Central Registry Verification',
    description: 'Wyoming requires Central Registry verification for all employees per Chapter 9',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 5(a)(ii)',
    regulationName: 'Central Registry Check Requirements',
    agencyName: 'Wyoming Department of Family Services',
    agencyAbbr: 'DFS',
    agencyPhone: '(307) 777-5894',
    agencyUrl: 'https://dfs.wyo.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify the Central Registry for ALL employees at time of hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before employment' },
          { value: 'sometimes', label: 'Sometimes, but not consistently' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Central Registry check required per Chapter 9, Section 5(a)(ii). Call (307) 777-5894.',
      },
      {
        id: 'q2',
        question: 'Do you ensure confidentiality of information obtained from the Central Registry?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Chapter 9, Section 5(a)(iii)(B)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Central Registry verification meets Chapter 9, Section 5(a)(ii) requirements.',
      atRisk: 'Central Registry checks are not consistent. Implement systematic verification.',
      nonCompliant: 'No Central Registry verification. This violates Chapter 9, Section 5(a)(ii). Contact DFS at (307) 777-5894.',
      unknown: 'Verify Central Registry check procedures.',
    },
    stateNotes: [
      'Central Registry maintained by DFS per W.S. 14-3-213',
      'Indexes perpetrators of child abuse/neglect and abuse/neglect/exploitation of disabled adults',
      'Contact: (307) 777-5894',
      'Must ensure confidentiality of information obtained',
      'Governing body responsible to initiate and follow process to completion',
    ],
  },

  {
    id: 'WY-B2',
    itemId: 'B2',
    stateCode: 'WY',
    title: 'Criminal Background Check',
    description: 'Wyoming requires criminal background checks for direct care staff',
    severity: 'high',
    regulationCode: 'WDH Background Screening Requirements',
    regulationName: 'Background Checks',
    agencyName: 'Wyoming Division of Criminal Investigation',
    agencyAbbr: 'DCI',
    agencyPhone: '(307) 777-7181',
    agencyUrl: 'https://health.wyo.gov/admin/background-screening/',
    questions: [
      {
        id: 'q1',
        question: 'For ALL direct care staff, which background checks do you complete? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'state', label: 'Wyoming State criminal background check (DCI)' },
          { value: 'federal', label: 'FBI national criminal background check (fingerprint-based)' },
          { value: 'central_registry', label: 'DFS Central Registry check' },
          { value: 'oig', label: 'HHS OIG Exclusion Database check' },
          { value: 'sex_offender', label: 'Sex Offender Registry check' },
        ],
        required: true,
        helpText: 'Comprehensive background screening includes DCI/FBI fingerprints, Central Registry, and HHS Exclusions',
      },
      {
        id: 'q2',
        question: 'Are background checks completed BEFORE employees have any patient contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - always before patient contact' },
          { value: 'no', label: 'No - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check documentation is complete for all direct care staff.',
      atRisk: 'Background check process may be incomplete. Verify all required screenings are being conducted.',
      nonCompliant: 'Staff working without proper background clearance. Immediately remove uncleared staff from patient assignments.',
      unknown: 'Audit background check procedures.',
    },
    stateNotes: [
      'Background screening fee: $49 per person',
      'Fingerprinting available at DCI in Cheyenne or local law enforcement',
      'DCI appointment: (307) 777-7181',
      'Results sent by secure email to facility HR (expires after 14 days)',
      'WDH provides 60 days to correct/complete background information',
    ],
  },

  {
    id: 'WY-B3',
    itemId: 'B3',
    stateCode: 'WY',
    title: 'OIG Exclusion List Verification',
    description: 'Federal requirement to verify employees against OIG exclusion list',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusion Requirements',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG Exclusion List (LEIE) for all employees before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before hire' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you conduct monthly OIG exclusion list checks?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Monthly checks recommended for Medicare/Medicaid providers',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'always', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['sometimes', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification is performed at hire and monthly.',
      atRisk: 'OIG checks at hire but monthly checks not implemented.',
      nonCompliant: 'No OIG exclusion list verification. This is required for Medicare/Medicaid providers.',
      unknown: 'Review OIG exclusion list verification requirements.',
    },
    stateNotes: [
      'OIG LEIE check required for all Medicare/Medicaid providers per 42 CFR 1001',
      'Free verification at https://exclusions.oig.hhs.gov/',
      'Monthly checks recommended to catch new exclusions',
      'Employing excluded individuals can result in civil monetary penalties',
      'Document all OIG verification checks with date and results',
    ],
  },

  // ============================================
  // SECTION C: PERSONNEL
  // ============================================
  {
    id: 'WY-C1',
    itemId: 'C1',
    stateCode: 'WY',
    title: 'Personnel Records',
    description: 'Wyoming requires specific personnel documentation per Chapter 9',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 5(a)(x)',
    regulationName: 'Employee Personnel Records',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following do you maintain in EVERY employee personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'application', label: 'Employment application' },
          { value: 'central_registry', label: 'Central Registry verification' },
          { value: 'background', label: 'Criminal background check results' },
          { value: 'credentials', label: 'Professional licenses and certifications' },
          { value: 'references', label: 'Reference verification' },
          { value: 'health', label: 'Health assessment (free of communicable diseases)' },
          { value: 'orientation', label: 'Orientation completion documentation' },
          { value: 'competency', label: 'Competency evaluations' },
          { value: 'training', label: 'Continuing education and training records' },
          { value: 'job_desc', label: 'Job description acknowledgment' },
          { value: 'evaluations', label: 'Performance evaluations' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is there one person designated responsible for maintaining confidentiality of personnel records?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Chapter 9, Section 5(a)(x)(A)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 10 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all required documentation per Chapter 9.',
      atRisk: 'Some personnel files are missing required documents.',
      nonCompliant: 'Personnel files are missing critical documentation.',
      unknown: 'Audit personnel files against Chapter 9 requirements.',
    },
    stateNotes: [
      'One person designated responsible for personnel record confidentiality per Section 5(a)(x)(A)',
      'Must maintain Central Registry and background check documentation',
      'Health assessment documenting freedom from communicable diseases required',
      'Training and competency documentation must be retained',
      'All records subject to WDH inspection during surveys',
    ],
  },

  {
    id: 'WY-C2',
    itemId: 'C2',
    stateCode: 'WY',
    title: 'Employee Health Requirements',
    description: 'Wyoming requires employees to be free of communicable diseases per Chapter 9',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 5(a)(xi)',
    regulationName: 'Employee Health Requirements',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have policies and procedures for employee health including communicable diseases?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per Chapter 9, Section 5(a)(xi)(A)',
      },
      {
        id: 'q2',
        question: 'Do you document that employees are free of communicable diseases that could be a risk to clients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per Chapter 9, Section 5(a)(xi)(B)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Employee health requirements meet Chapter 9, Section 5(a)(xi).',
      atRisk: 'Employee health documentation may be incomplete.',
      nonCompliant: 'No employee health policies. This violates Chapter 9, Section 5(a)(xi).',
      unknown: 'Review employee health requirements.',
    },
    stateNotes: [
      'Policy must identify communicable diseases that could put client population at risk',
      'Must document employee is free of communicable diseases',
      'Policy should address when employees should not work',
    ],
  },

  {
    id: 'WY-C3',
    itemId: 'C3',
    stateCode: 'WY',
    title: 'Professional License Verification',
    description: 'Wyoming requires verification of all professional licenses',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 4',
    regulationName: 'License Verification Requirements',
    agencyName: 'Wyoming State Board of Nursing',
    agencyAbbr: 'WSBN',
    agencyPhone: '(307) 777-7601',
    agencyUrl: 'https://wsbn.wyo.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses (RN, LPN, PT, OT, ST, etc.) before hiring?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified with appropriate board' },
          { value: 'sometimes', label: 'Sometimes verified' },
          { value: 'no', label: 'No verification process' },
        ],
        required: true,
        helpText: 'All staff must be licensed per Wyoming State Statutes (Chapter 9, Section 4(m))',
      },
      {
        id: 'q2',
        question: 'Do you verify CNA certification with the Wyoming State Board of Nursing?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'CNA verification required per Chapter 9, Section 4(c)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification is comprehensive.',
      atRisk: 'License verification is inconsistent. Implement systematic verification.',
      nonCompliant: 'No license verification process violates Wyoming professional practice requirements.',
      unknown: 'Review license verification process.',
    },
    stateNotes: [
      'Wyoming State Board of Nursing: https://wsbn.wyo.gov/ - (307) 777-7601',
      'RN/LPN license verification available online',
      'CNA certification must be verified per Chapter 9, Section 4(c)',
      'All staff must be licensed per Wyoming State Statutes per Section 4(m)',
      'Document verification date and method in personnel file',
    ],
  },

  // ============================================
  // SECTION D: HOME HEALTH AIDE
  // ============================================
  {
    id: 'WY-D1',
    itemId: 'D1',
    stateCode: 'WY',
    title: 'Home Health Aide Qualifications',
    description: 'Wyoming requires specific qualifications for home health aides per Chapter 9',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 6',
    regulationName: 'Home Health Aide Requirements',
    agencyName: 'Wyoming State Board of Nursing',
    agencyAbbr: 'WSBN',
    agencyPhone: '(307) 777-7601',
    agencyUrl: 'https://wsbn.wyo.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your home health aides meet Wyoming qualification requirements?',
        inputType: 'checklist',
        options: [
          { value: 'cna', label: 'CNA certified by Wyoming Board of Nursing' },
          { value: 'cna_verified', label: 'CNA certification verified by administrator or designee' },
          { value: 'hh_training', label: '16 hours additional training on home health issues (Board-approved)' },
          { value: 'competency', label: 'Competency in home setting documented' },
          { value: 'training_file', label: 'Training documentation retained in personnel record' },
        ],
        required: true,
        helpText: 'Requirements per Chapter 9, Section 6(a) and Section 4(p)',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All home health aides meet Wyoming qualification requirements per Chapter 9, Section 6.',
      atRisk: 'Some HHAs may not meet all qualification requirements. Review credentials.',
      nonCompliant: 'HHAs working without proper qualifications violates Chapter 9, Section 6.',
      unknown: 'Verify all HHA qualifications.',
    },
    stateNotes: [
      'HHA must be CNA + 16 hours additional home health training (Chapter 9, Section 4(p))',
      'Training must be approved by Wyoming Board of Nursing',
      'CNA certification must be verified by administrator or designee',
      'Training must ensure competency in home setting and be documented',
    ],
  },

  {
    id: 'WY-D2',
    itemId: 'D2',
    stateCode: 'WY',
    title: 'HHA Supervision - Skilled Services',
    description: 'Wyoming requires RN or LPN supervision of HHAs every 30 days for skilled services',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 6(b)',
    regulationName: 'HHA Supervision Requirements - Skilled',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'For clients requiring SKILLED services, are home health aides supervised by an RN or LPN at least every 30 days?',
        inputType: 'select',
        options: [
          { value: 'every_30', label: 'Yes, at least every 30 days (meets Chapter 9, Section 6(b))' },
          { value: 'less_frequent', label: 'Less frequently than every 30 days' },
          { value: 'no', label: 'No supervision schedule' },
        ],
        required: true,
        helpText: 'Required per Chapter 9, Section 6(b)',
      },
      {
        id: 'q2',
        question: 'Is supervisory visit documentation maintained?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'every_30' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'less_frequent', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA supervision for skilled services meets Chapter 9, Section 6(b) requirements.',
      atRisk: 'HHA supervision may not meet 30-day frequency requirement.',
      nonCompliant: 'Inadequate HHA supervision violates Chapter 9, Section 6(b).',
      unknown: 'Review HHA supervision schedule.',
    },
    stateNotes: [
      'If client requires skilled services: HHA supervised by RN or LPN at least every 30 days',
      'This is for clients receiving skilled nursing, PT, OT, ST, etc.',
    ],
  },

  {
    id: 'WY-D3',
    itemId: 'D3',
    stateCode: 'WY',
    title: 'HHA Care Plan Instructions',
    description: 'Wyoming requires supervisory nurse to provide HHA instructions every 60 days',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 6(d)',
    regulationName: 'HHA Care Plan Instruction Requirements',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Does the supervisory nurse provide written care plan instructions to home health aides?',
        inputType: 'select',
        options: [
          { value: 'every_60', label: 'Yes, at least every 60 days or as condition warrants' },
          { value: 'less_frequent', label: 'Less frequently than every 60 days' },
          { value: 'no', label: 'No structured instruction process' },
        ],
        required: true,
        helpText: 'Required per Chapter 9, Section 6(d)',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'every_60', result: 'COMPLIANT' },
      { condition: 'equals', value: 'less_frequent', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA care plan instructions meet Chapter 9, Section 6(d) requirements.',
      atRisk: 'Care plan instructions may not meet 60-day frequency requirement.',
      nonCompliant: 'No structured care plan instruction process violates Chapter 9, Section 6(d).',
      unknown: 'Review care plan instruction process.',
    },
    stateNotes: [
      'Written care plan instructions to HHA at least every 60 days',
      'Or as client\'s condition warrants (more frequently if needed)',
      'Instructions must be based on written care plans',
    ],
  },

  // ============================================
  // SECTION E: HOMEMAKER SERVICES
  // ============================================
  {
    id: 'WY-E1',
    itemId: 'E1',
    stateCode: 'WY',
    title: 'Homemaker Services',
    description: 'Wyoming regulates homemaker services per Chapter 9, Section 7',
    severity: 'medium',
    regulationCode: 'Chapter 9, Section 7',
    regulationName: 'Homemaker Services Requirements',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide homemaker services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'If you provide homemaker services, do they comply with Wyoming requirements?',
        inputType: 'checklist',
        options: [
          { value: 'no_personal_care', label: 'Homemakers do NOT provide personal care (only instrumental ADLs)' },
          { value: 'min_8_hours', label: 'Minimum 8 hours training before client assignment' },
          { value: 'training_documented', label: 'Training documented in personnel record' },
          { value: 'written_plan', label: 'Written service plan for homemaker-only clients' },
          { value: 'supervision_90', label: 'Supervisory visit by RN or LPN at least every 90 days' },
          { value: 'homemaker_present', label: 'Homemaker present during supervisory visits' },
        ],
        helpText: 'Requirements per Chapter 9, Section 7',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Homemaker services meet Chapter 9, Section 7 requirements.',
      atRisk: 'Homemaker services may not meet all requirements.',
      nonCompliant: 'Homemaker services do not comply with Chapter 9, Section 7.',
      unknown: 'Review homemaker requirements if providing these services.',
    },
    stateNotes: [
      'Homemakers provide ONLY instrumental ADLs (housekeeping, shopping, laundry, basic meals, errands)',
      'Homemakers do NOT provide personal care',
      'Minimum 8 hours training before client assignment (Section 7(c))',
      'Training includes: orientation, working with populations, communication, homemaking skills, universal precautions, emergency procedures, client rights',
      'Supervision by RN or LPN at least every 90 days for unskilled services',
      'Homemaker must be present during supervisory visits',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'WY-F1',
    itemId: 'F1',
    stateCode: 'WY',
    title: 'Plan of Care Requirements',
    description: 'Wyoming requires individualized plans of care per Chapter 9',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 4(z)',
    regulationName: 'Plan of Care',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY client before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your plans of care include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'diagnoses', label: 'Diagnoses' },
          { value: 'medications', label: 'Medications' },
          { value: 'services', label: 'Types of services' },
          { value: 'equipment', label: 'Equipment needed' },
          { value: 'visit_protocol', label: 'Visit protocol' },
          { value: 'limitations', label: 'Functional limitations' },
          { value: 'activity', label: 'Activity level' },
          { value: 'nutrition', label: 'Nutrition requirements' },
          { value: 'treatments', label: 'Treatments' },
          { value: 'safety', label: 'Safety issues' },
        ],
        helpText: 'Required elements per Chapter 9, Section 4(z)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 9 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of care meet Chapter 9 requirements.',
      atRisk: 'Plans of care may be incomplete or created after services begin.',
      nonCompliant: 'Services provided without written plans of care violates Chapter 9.',
      unknown: 'Review plan of care process.',
    },
    stateNotes: [
      'Plan of care must be individualized and address at minimum: diagnoses, medications, services, equipment, visit protocol, functional limitations, activity level, nutrition, treatments, safety issues',
      'Any other information necessary to care for the client',
    ],
  },

  {
    id: 'WY-F2',
    itemId: 'F2',
    stateCode: 'WY',
    title: 'Electronic Visit Verification (EVV)',
    description: 'EVV required for Medicaid personal care and home health services',
    severity: 'high',
    regulationCode: '21st Century Cures Act / Wyoming Medicaid EVV Requirements',
    regulationName: 'EVV',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/healthcarefin/medicaid/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Medicaid-funded personal care or home health services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use a Wyoming-approved EVV system to document visits?',
        inputType: 'select',
        options: [
          { value: 'yes_compliant', label: 'Yes, fully implemented and compliant' },
          { value: 'yes_partial', label: 'Yes, but not for all visits' },
          { value: 'no', label: 'No EVV system in place' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'EVV required for Medicaid personal care and home health services per federal law',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_compliant', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV system is properly implemented for Wyoming Medicaid services.',
      atRisk: 'EVV is not being used consistently. Ensure 100% EVV compliance.',
      nonCompliant: 'Wyoming Medicaid services require EVV per federal law. Contact WDH Medicaid.',
      unknown: 'If you provide Medicaid services, verify EVV compliance.',
    },
    stateNotes: [
      '21st Century Cures Act mandates EVV for personal care and home health services',
      'Wyoming Medicaid EVV requirements effective January 1, 2021',
      'Contact WDH Medicaid for approved EVV vendor list',
      'EVV must capture: type of service, individual receiving, individual providing, date, location, and time',
      'Non-compliance can result in Medicaid payment reductions',
    ],
  },

  // ============================================
  // SECTION G: CLIENT RECORDS
  // ============================================
  {
    id: 'WY-G1',
    itemId: 'G1',
    stateCode: 'WY',
    title: 'Client Record Requirements',
    description: 'Wyoming requires comprehensive client records per Chapter 9, Section 8',
    severity: 'high',
    regulationCode: 'Chapter 9, Section 8',
    regulationName: 'Client Records Requirements',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Is a record maintained for every client receiving services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per Chapter 9, Section 8(a)',
      },
      {
        id: 'q2',
        question: 'Are client records safeguarded against loss or unauthorized use?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per Chapter 9, Section 8(c)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client records meet Chapter 9, Section 8 requirements.',
      atRisk: 'Client records may not be adequately safeguarded.',
      nonCompliant: 'Inadequate client records violates Chapter 9, Section 8.',
      unknown: 'Review client record requirements.',
    },
    stateNotes: [
      'Record required for every client receiving services per Chapter 9, Section 8(a)',
      'Records must be safeguarded against loss or unauthorized use per Section 8(c)',
      'Must retain records for 6 years after discharge per Section 8(b)',
      'Records subject to WDH inspection during surveys',
      'HIPAA compliance required for all protected health information',
    ],
  },

  {
    id: 'WY-G2',
    itemId: 'G2',
    stateCode: 'WY',
    title: 'Record Retention',
    description: 'Wyoming requires 6-year retention of client and personnel records',
    severity: 'medium',
    regulationCode: 'Chapter 9, Section 8(b) / Chapter 10, Section 5(h)(iii)',
    regulationName: 'Record Retention',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain client records?',
        inputType: 'select',
        options: [
          { value: '6plus', label: '6 years or longer' },
          { value: '3to5', label: '3-5 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Wyoming requires 6-year retention per Chapter 9, Section 8(b)',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '6plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '3to5', result: 'AT_RISK' },
      { condition: 'includes', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets Wyoming 6-year requirement.',
      atRisk: 'Wyoming requires 6-year record retention. Update your policy.',
      nonCompliant: 'Record retention does not meet Wyoming requirements. Records must be retained 6 years.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      'Client records: retained 6 years by the agency (Chapter 9, Section 8(b))',
      'Upon closure: personnel, financial, and client records retained minimum 6 years after closure month (Chapter 10, Section 5(h)(iii))',
    ],
  },

  // ============================================
  // SECTION H: SURVEY AND COMPLIANCE
  // ============================================
  {
    id: 'WY-H1',
    itemId: 'H1',
    stateCode: 'WY',
    title: 'Survey Deficiency Response',
    description: 'Wyoming requires plan of correction within 10 working days per Chapter 10',
    severity: 'high',
    regulationCode: 'Chapter 10, Section 5(g)(C)',
    regulationName: 'Survey Response Requirements',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'When you receive survey deficiencies, do you submit a plan of correction within 10 working days?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within 10 working days' },
          { value: 'usually', label: 'Usually, but sometimes delayed' },
          { value: 'na', label: 'N/A - no recent deficiencies' },
        ],
        required: true,
        helpText: 'Required per Chapter 10, Section 5(g)(C)',
      },
      {
        id: 'q2',
        question: 'Do your plans of correction include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'who', label: 'Who is responsible for the correction' },
          { value: 'what', label: 'What was/will be done to correct the problem' },
          { value: 'monitor', label: 'Who will monitor to ensure situation does not recur' },
          { value: 'date', label: 'Date for correction (not to exceed 60 days after survey)' },
        ],
        helpText: 'Required elements per Chapter 10, Section 4(a)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'na', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Survey deficiency response meets Chapter 10 requirements.',
      atRisk: 'Plans of correction may not be submitted timely or complete.',
      nonCompliant: 'Failure to submit timely plan of correction can result in license action.',
      unknown: 'Review survey response requirements.',
    },
    stateNotes: [
      'Plan of correction due within 10 working days after receipt of deficiencies',
      'Correction date not to exceed 60 days after last day of survey',
      'Licensing Division provides written notification of acceptance within 10 working days',
    ],
  },

  {
    id: 'WY-H2',
    itemId: 'H2',
    stateCode: 'WY',
    title: 'Medicare/Medicaid Certification',
    description: 'Wyoming certification for Medicare/Medicaid participation',
    severity: 'medium',
    regulationCode: 'Chapter 10 / 42 CFR 484',
    regulationName: 'Medicare/Medicaid Certification Requirements',
    agencyName: 'Wyoming Department of Health, Healthcare Licensing and Surveys',
    agencyAbbr: 'WDH HLS',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/aging/hls/',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency Medicare and/or Medicaid certified?',
        inputType: 'select',
        options: [
          { value: 'both', label: 'Yes, both Medicare and Medicaid certified' },
          { value: 'medicare_only', label: 'Medicare certified only' },
          { value: 'medicaid_only', label: 'Medicaid certified only' },
          { value: 'neither', label: 'Not certified (state license only)' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you notify prospective clients if you are NOT Medicare/Medicaid certified?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
          { value: 'na', label: 'N/A - we are certified' },
        ],
        helpText: 'Required per Chapter 9, Section 5(a)(xiv)(A)',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'neither',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'includes', questionId: 'q1', value: ['both', 'medicare_only', 'medicaid_only'], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'neither' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Certification status is appropriate and disclosed as required.',
      atRisk: 'N/A',
      nonCompliant: 'Must notify prospective clients if not Medicare/Medicaid certified per Chapter 9.',
      unknown: 'Review certification disclosure requirements.',
    },
    stateNotes: [
      'State license required BEFORE providing care',
      'Medicare certification requires meeting Conditions of Participation (42 CFR 484)',
      'HLS certifies and recertifies for Medicare/Medicaid participation',
      'Must notify clients prior to admission if not certified (Chapter 9, Section 5(a)(xiv)(A))',
      'Medicare certification via CMS-855A or CMS-855B application',
    ],
  },

  {
    id: 'WY-H3',
    itemId: 'H3',
    stateCode: 'WY',
    title: 'HIPAA Compliance',
    description: 'Federal HIPAA requirements for privacy and security',
    severity: 'high',
    regulationCode: '45 CFR Parts 160, 162, 164',
    regulationName: 'HIPAA Privacy and Security Rules',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a comprehensive HIPAA compliance program?',
        inputType: 'checklist',
        options: [
          { value: 'privacy', label: 'Written privacy policies and procedures' },
          { value: 'security', label: 'Security policies for electronic PHI' },
          { value: 'officer', label: 'Designated Privacy Officer' },
          { value: 'training', label: 'Staff training on HIPAA requirements' },
          { value: 'breach', label: 'Breach notification procedures' },
          { value: 'baa', label: 'Business Associate Agreements with vendors' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA compliance program is comprehensive.',
      atRisk: 'HIPAA compliance program may be incomplete.',
      nonCompliant: 'HIPAA compliance program is inadequate. This is a federal requirement.',
      unknown: 'Review HIPAA compliance requirements.',
    },
    stateNotes: [
      'HIPAA Privacy Rule: 45 CFR Part 164 Subpart E',
      'HIPAA Security Rule: 45 CFR Part 164 Subpart C',
      'Must designate Privacy Officer responsible for compliance',
      'Staff training required on HIPAA requirements',
      'Breach notification required within 60 days per 45 CFR 164.404',
    ],
  },

  // ============================================
  // SECTION I: TRAINING
  // ============================================
  {
    id: 'WY-I1',
    itemId: 'I1',
    stateCode: 'WY',
    title: 'Abuse Recognition and Reporting Training',
    description: 'Wyoming requires training on abuse recognition and mandatory reporting',
    severity: 'high',
    regulationCode: 'W.S. 35-20-103',
    regulationName: 'Adult Protective Services - Mandatory Reporter Training',
    agencyName: 'Wyoming Department of Family Services',
    agencyAbbr: 'DFS',
    agencyPhone: '(307) 777-5894',
    agencyUrl: 'https://dfs.wyo.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Are ALL employees trained on abuse recognition and mandatory reporting?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees are trained' },
          { value: 'some', label: 'Some employees are trained' },
          { value: 'no', label: 'No formal abuse training' },
        ],
        required: true,
        helpText: 'Wyoming Adult Protective Services Act (W.S. 35-20-101 et seq.)',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All employees are trained on abuse recognition and reporting.',
      atRisk: 'Abuse recognition training is inconsistent.',
      nonCompliant: 'No formal abuse recognition training. This is required.',
      unknown: 'Review abuse reporting training requirements.',
    },
    stateNotes: [
      'Wyoming Adult Protective Services Act: W.S. 35-20-101 through 35-20-116',
      'Mandatory reporters include healthcare workers per W.S. 35-20-103',
      'Reports to DFS: (307) 777-5894 or 1-800-457-3659',
      'Child abuse reporting per W.S. 14-3-201 et seq.',
      'Training must cover recognition of abuse, neglect, and exploitation',
    ],
  },

  {
    id: 'WY-I2',
    itemId: 'I2',
    stateCode: 'WY',
    title: 'Infection Control Training',
    description: 'Wyoming requires infection control training for all staff',
    severity: 'high',
    regulationCode: 'Chapter 9 / Chapter 10',
    regulationName: 'Infection Control Requirements',
    agencyName: 'Wyoming Department of Health',
    agencyAbbr: 'WDH',
    agencyPhone: '(307) 777-7123',
    agencyUrl: 'https://health.wyo.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff receive infection control training including universal precautions?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff are trained' },
          { value: 'some', label: 'Some staff are trained' },
          { value: 'no', label: 'No formal infection control training' },
        ],
        required: true,
        helpText: 'Universal precautions training required for homemakers (Chapter 9, Section 7(c)(ii)(G))',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff receive infection control training.',
      atRisk: 'Infection control training is inconsistent.',
      nonCompliant: 'No infection control training program.',
      unknown: 'Review infection control training requirements.',
    },
    stateNotes: [
      'Universal precautions training required for homemakers per Chapter 9, Section 7(c)(ii)(G)',
      'Training must cover hand hygiene, PPE use, and standard precautions',
      'OSHA Bloodborne Pathogen Standard (29 CFR 1910.1030) applies',
      'Document training completion with date and signature',
      'Annual refresher training recommended',
    ],
  },
];

export default WYOMING_QUESTIONS;